/**
 * A screener section for a layout that has none.
 *
 * Every other way to get a screener assumes one already exists: `placeScreener`
 * *moves* the section it finds, the layout panel's four sides are hidden when
 * there is nothing to dock, and the strip's slot only offers to reveal a panel
 * that is merely hidden. So a layout built as two charts could never gain one —
 * not from the strip, not from the panel, not at all. This is the factory that
 * closes that, and `addScreener` in `layout-templates.ts` is what places it.
 */

import {
  mintUlid,
  type ScreenerColumn,
  type ScreenerConfig,
  type Section,
  type UserWorkspace,
} from "@qufolio/model";
import { LOGO_COLUMN } from "@qufolio/screener-grid";

/**
 * The screener to copy a new one from: the first one in the workspace.
 *
 * Seeded rather than minted blank for `makeChart`'s reason — a new chart takes
 * the first chart's symbol instead of being a stranger — and it matters more
 * here. A screener is a group, a screen, a column set, a sort and a filter
 * stack, all of which someone chose; a blank one would technically satisfy "this
 * layout has a screener" while showing none of the columns they work with.
 *
 * Across *every* layout, not just this one, because the layout being asked about
 * is by definition the one with no screener to copy.
 */
export function screenerSeed(workspace: UserWorkspace | undefined): ScreenerConfig | undefined {
  for (const layout of workspace?.layouts ?? []) {
    for (const section of layout.sections) {
      if (section.kind === "screener" && section.config) return section.config as ScreenerConfig;
    }
  }
  return undefined;
}

/**
 * The group and screen a workspace with no screener anywhere falls back to.
 *
 * The same pair the demo workspace ships, because the socket URL is
 * `/{group}/{screen}` and there is no such thing as a screener without them —
 * an empty string would open a connection that can only fail. A first-run user
 * gets the same starting point the demo does and changes it from the editor.
 */
const FALLBACK = { group: "SP500", screen: "JASDOTS" } as const;

/**
 * What a screener minted from nothing shows: which company, where it belongs,
 * and what it has done today.
 *
 * **A column view is supplied deliberately**, for the reason the demo
 * workspace's is: the result carries well over a hundred columns and the server
 * sends all of them, so an empty view renders every one — correct, and unusable
 * as a first impression. Everything unlisted stays one click away in the column
 * menu.
 *
 * **Four are shown**: `symbol`, and then `close`, `changePercent` and `change`
 * — which row you are looking at, what it costs now, and what it has done today
 * in both the units people compare across names and the units they feel.
 * `sector` used to be a fifth and is not: where a company sits is a thing you
 * go looking for, not a thing you read on every row, and it cost 150px of a
 * first impression.
 *
 * `logo` stays, and is not really a fifth column — it has no header and is
 * pinned beside the ticker, so the pair reads as one cell. Dropping it would be
 * a change to how a row looks rather than to how many columns it has.
 *
 * **Nine more are listed and unticked**: name, sector, industry, the rest of the
 * bar, revenue and EPS. They are not on screen, and they are not meant to be —
 * what they are is *offered*, sitting in the editor's Columns tab with an empty
 * box beside each, so turning one on is a click rather than a hunt through a
 * catalog of ninety.
 *
 * The contract makes them reachable either way — an unlisted field "remains
 * available to show but stays hidden until added" — so this is not about
 * reachability. It is about which nine a person meets first, and about their
 * widths and notation being right the moment one is ticked rather than each
 * arriving raw.
 *
 * **This is only useful because the editor no longer lists the catalog.** While
 * that tab rendered every field the server sends, these nine were nine rows
 * among ninety and invisible; the fix for that was the tab, not this list, and
 * removing them once in the belief that it was this list was a mistake.
 *
 * So: four on screen, nine offered, and **everything else, computed columns
 * included, one click away in the Columns popover**.
 *
 * The shapes are copied from `demo-workspace.ts` rather than simplified, so a
 * screener someone creates and the one they were shown format the same numbers
 * the same way. The colouring uses the **same** `positive`/`negative` tokens the
 * candles do, so an up day and an up candle are one green by construction (D13).
 */
export const DEFAULT_COLUMNS: readonly ScreenerColumn[] = [
  LOGO_COLUMN,
  { field: "symbol", header: "Symbol", pinned: "left", width: 90 },
  { field: "close", header: "Last", precision: 2, width: 90 },
  {
    field: "changePercent",
    header: "Chg %",
    precision: 2,
    width: 90,
    format: [
      { when: { op: "gte", value: 0 }, style: { fg: "positive" } },
      { when: { op: "lt", value: 0 }, style: { fg: "negative" } },
    ],
  },
  {
    field: "change",
    header: "Chg",
    precision: 2,
    width: 85,
    format: [
      { when: { op: "gte", value: 0 }, style: { fg: "positive" } },
      { when: { op: "lt", value: 0 }, style: { fg: "negative" } },
    ],
  },

  // ── Offered, not shown: unticked in the editor, one click from the grid ────
  { field: "name", header: "Name", width: 190, hide: true },
  { field: "sector", header: "Sector", width: 150, hide: true },
  { field: "industry", header: "Industry", width: 180, hide: true },
  { field: "open", header: "Open", precision: 2, width: 85, hide: true },
  { field: "high", header: "High", precision: 2, width: 85, hide: true },
  { field: "low", header: "Low", precision: 2, width: 85, hide: true },
  // Compact: share counts run to nine figures, and revenue further still — a
  // raw one is a number nobody reads across rows.
  { field: "volume", header: "Vol", precision: 0, notation: "compact", width: 95, hide: true },
  { field: "revenue", header: "Revenue", precision: 0, notation: "compact", width: 100, hide: true },
  { field: "eps", header: "EPS", precision: 2, width: 80, hide: true },
];
/**
 * Mint a screener section, copying `seed` if there is one.
 *
 * The **id is always fresh**: the config carries one, and two sections sharing
 * it would collide in the relational store, where a screener is addressed by
 * that id rather than by which layout it happens to sit in. Everything else is
 * copied wholesale rather than field by field, so a column type added to the
 * contract tomorrow comes along without this function being taught about it.
 *
 * Position is left at the origin — `addScreener` runs the whole layout through
 * `applyTemplate`, which assigns every section its cell from the arrangement.
 * Anything set here would be overwritten a moment later.
 */
export function newScreenerSection(seed?: ScreenerConfig): Section {
  const config: ScreenerConfig = seed
    ? { ...seed, id: mintUlid() }
    : { id: mintUlid(), ...FALLBACK, columns: [...DEFAULT_COLUMNS] };

  return {
    id: mintUlid(),
    kind: "screener",
    row: 0,
    column: 0,
    rowSpan: 1,
    columnSpan: 1,
    title: "Screener",
    config,
  };
}
